import type { FC } from 'react';
import type { FormRule } from 'antd';
import { Col, DatePicker, Form, Input, InputNumber, Row, Select } from 'antd';
import dayjs from 'dayjs';
import { useGetCargoItems } from 'api/cargo-items/use-get-cargo-items';
import { formatCargoData } from 'shared/utils/cargos/format-cargo-data';
import { groupCargosBySize } from 'shared/utils/cargos/group-cargos-by-size';
import { CURRENCIES, ECurrency } from 'types/currencies';
import type {
  ITripSoldPdfGeneratorFormFields,
  ITripSoldPdfGeneratorFormProps,
} from 'components/trips/trip-sold-pdf-generation/components/trip-sold-pdf-generator-form/types';
import styles from './trip-sold-pdf-generator-form.module.scss';
import { formatAddress } from './utils';

const { Option } = Select;

const validationRule: Record<keyof ITripSoldPdfGeneratorFormFields, FormRule[]> = {
  cargoData: [{ required: true, message: 'Please input cargo data' }],
  sendersData: [{ required: true, message: "Please input sender's data" }],
  recipientData: [{ required: true, message: "Please input recipient's data" }],
  loadingDate: [{ required: true, message: 'Please input loading date' }],
  unloadingDate: [{ required: true, message: 'Please input unloading date' }],
  price: [{ required: true, message: 'Please input price' }],
  amountOfDays: [{ required: true, message: 'Please input the amount of days' }],
  currency: [
    {
      required: true,
      message: 'Please select currency',
    },
  ],
  exportCustomOffice: [],
  importCustomOffice: [],
  remarks: [],
};

export const TripSoldPdfGeneratorForm: FC<ITripSoldPdfGeneratorFormProps> = (props) => {
  const { trip, form, formId, onFinish, cargoTrip } = props;
  const { data: cargoItems, isLoading } = useGetCargoItems({ cargoId: cargoTrip.cargoId });

  const loadingAddressString = formatAddress(cargoTrip.cargo.loadingAddress);
  const unloadingAddressString = formatAddress(cargoTrip.cargo.unloadingAddress);
  const groupedCargoItems = groupCargosBySize(cargoItems?.data || []);
  const cargoDataString = formatCargoData(groupedCargoItems, cargoTrip.cargo);

  const initialValues: ITripSoldPdfGeneratorFormFields = {
    cargoData: cargoDataString,
    sendersData: loadingAddressString,
    recipientData: unloadingAddressString,
    exportCustomOffice: '',
    importCustomOffice: '',
    loadingDate: dayjs(cargoTrip?.cargo?.loadingDateFrom),
    unloadingDate: dayjs(cargoTrip?.cargo?.unloadingDateFrom),
    remarks: '',
    price: trip.costPrice,
    currency: ECurrency.EUR,
    amountOfDays: trip.carrier?.paymentPeriod,
  };

  if (isLoading) {
    return null;
  }

  return (
    <Form form={form} id={formId} layout='vertical' initialValues={initialValues} onFinish={onFinish}>
      <Form.Item label='Cargo Data' name='cargoData' rules={validationRule.cargoData}>
        <Input.TextArea rows={4} />
      </Form.Item>

      <Form.Item label="Sender's Data" name='sendersData' rules={validationRule.sendersData}>
        <Input.TextArea />
      </Form.Item>

      <Form.Item label='Export Custom Office' name='exportCustomOffice'>
        <Input />
      </Form.Item>

      <Form.Item label='Recipient Data' name='recipientData' rules={validationRule.recipientData}>
        <Input.TextArea />
      </Form.Item>

      <Form.Item label='Import Custom Office' name='importCustomOffice'>
        <Input />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label='Loading Date' name='loadingDate' rules={validationRule.loadingDate}>
            <DatePicker />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label='Unloading Date' name='unloadingDate' rules={validationRule.unloadingDate}>
            <DatePicker />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label='Remarks' name='remarks'>
        <Input.TextArea />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label='Price' name='price' rules={validationRule.price}>
            <InputNumber type='number' min={0} className={styles.fullWidth} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label='Currency' name='currency' rules={validationRule.currency}>
            <Select>
              {CURRENCIES.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label='Amount of Days' name='amountOfDays' rules={validationRule.amountOfDays}>
        <InputNumber min={1} width={100} className={styles.fullWidth} />
      </Form.Item>
    </Form>
  );
};
